import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  Pagination,
  IconButton,
  InputAdornment,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import { 
  Search, 
  Clear, 
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Archive as ArchiveIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import { GET_PROPERTIES_WITH_RESERVATIONS, RESTORE_PROPERTY } from '../graphql/queries';
import AreaFilter from '../components/schedule/filters/AreaFilter';
import { useAuth } from '../contexts/AuthContext';
import { useMutation } from '@apollo/client';

const ITEMS_PER_PAGE = 40;

// Utility function that can be used by other components to navigate back to Properties
// with preserved pagination state
export const navigateBackToProperties = (navigate, page = 1, search = '', areas = []) => {
  navigate('/dashboard/properties', {
    state: {
      returnToPage: page,
      returnToSearch: search,
      returnToAreas: areas
    }
  });
};

const Properties = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  
  // Mutation for restoring properties (only needed for archived properties page)
  const [restoreProperty] = useMutation(RESTORE_PROPERTY);
  
  console.log('Properties: isMobile =', isMobile);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [accordionExpanded, setAccordionExpanded] = useState(false); // Closed by default on mobile
  const [includeArchived, setIncludeArchived] = useState(false);

  // Initialize with user's areas if none are selected - but only after auth is complete
  React.useEffect(() => {
    // Don't initialize while auth is still loading
    if (authLoading) {
      return;
    }
    
    // Only auto-initialize once when the component first loads and auth is complete
    if (!hasInitialized) {
      if (user?.areas && Array.isArray(user.areas) && user.areas.length > 0) {
        // Only set user areas if none are currently selected and no state is being restored
        if (selectedAreas.length === 0 && !location.state?.returnToAreas) {
          console.log('Properties: Auto-initializing with user assigned areas:', user.areas);
          setSelectedAreas(user.areas);
        }
      }
      // Mark as initialized only after auth loading is complete
      setHasInitialized(true);
    }
  }, [user, authLoading, hasInitialized, selectedAreas.length, location.state?.returnToAreas]);

  // Set accordion expanded state based on screen size
  React.useEffect(() => {
    setAccordionExpanded(!isMobile);
  }, [isMobile]);

  // Restore pagination state when returning from edit page
  React.useEffect(() => {
    if (location.state?.returnToPage || location.state?.returnToSearch || location.state?.returnToAreas) {
      if (location.state.returnToPage) {
        setCurrentPage(location.state.returnToPage);
      }
      if (location.state.returnToSearch) {
        setSearch(location.state.returnToSearch);
      }
      if (location.state.returnToAreas) {
        setSelectedAreas(location.state.returnToAreas);
      }
      // Clear the state so it doesn't persist on subsequent visits
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const { loading, error, data, networkStatus, refetch } = useQuery(GET_PROPERTIES_WITH_RESERVATIONS, {
    variables: {
      input: {
      limit: ITEMS_PER_PAGE || 10,
      page: currentPage || 1,
      search: search || undefined,
        areas: selectedAreas.length > 0 ? selectedAreas : undefined,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      includeArchived: includeArchived
      }
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const properties = data?.propertiesWithReservations?.properties || [];
  const totalItems = data?.propertiesWithReservations?.total || 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearch('');
    setCurrentPage(1);
  };

  const handleAreasChange = (areas) => {
    setSelectedAreas(areas);
    setCurrentPage(1);
  };

  const handleIncludeArchivedChange = (event) => {
    setIncludeArchived(event.target.checked);
    setCurrentPage(1);
  };


  const handleRestoreProperty = async (propertyId) => {
    try {
      await restoreProperty({
        variables: { id: propertyId },
        refetchQueries: [{ query: GET_PROPERTIES_WITH_RESERVATIONS, variables: { input: {
          limit: ITEMS_PER_PAGE,
          page: currentPage,
          search: search || undefined,
          areas: selectedAreas.length > 0 ? selectedAreas : undefined,
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          includeArchived: includeArchived
        }}}]
      });
    } catch (error) {
      console.error('Error restoring property:', error);
    }
  };

  const handleAddPropertyClick = () => {
    navigate('/dashboard/properties/create', {
      state: {
        returnToPage: currentPage,
        returnToSearch: search,
        returnToAreas: selectedAreas
      }
    });
  };

  if (loading && networkStatus === 1) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={2}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>Error loading properties</Typography>
          <Typography>{error.message}</Typography>
          {error.graphQLErrors?.map((err, index) => (
            <Typography key={index} color="error">GraphQL Error: {err.message}</Typography>
          ))}
          {error.networkError && (
            <Typography color="error">Network Error: {error.networkError.message}</Typography>
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box my={4}>
        {/* Header - White background on mobile */}
        <div 
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            backgroundColor: isMobile ? 'white' : 'transparent',
            padding: isMobile ? '5px' : '0',
            margin: isMobile ? '0 -5px 24px -5px' : '0 0 24px 0',
            borderRadius: isMobile ? '8px' : '0',
            boxShadow: isMobile ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          }}
        >
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
            Properties {loading && networkStatus !== 1 && <CircularProgress size={20} sx={{ ml: 2 }} />}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              component={Link}
              to="/dashboard/properties/archived"
              startIcon={<ArchiveIcon />}
              variant="outlined"
              color="secondary"
              size={isMobile ? "small" : "medium"}
              sx={{ minWidth: { xs: 'auto', md: 'auto' } }}
            >
              {isMobile ? 'Archived' : 'View Archived'}
            </Button>
            {!['MAINTENANCE', 'CALL_CENTER'].includes(user?.role) && (
              <Button
                onClick={handleAddPropertyClick}
                startIcon={<AddIcon />}
                variant="contained"
                color="primary"
                size={isMobile ? "small" : "medium"}
                sx={{ minWidth: { xs: 'auto', md: 'auto' } }}
              >
                {isMobile ? 'Add' : 'Add Property'}
              </Button>
            )}
          </Box>
        </div>

        {/* Search and Filter Section - Accordion on mobile */}
        <Box mb={3}>
          {isMobile ? (
            <Accordion 
              expanded={accordionExpanded} 
              onChange={(event, isExpanded) => setAccordionExpanded(isExpanded)}
              elevation={0}
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                '&:before': { display: 'none' },
                '& .MuiAccordionSummary-root': {
                  backgroundColor: 'grey.50',
                  borderRadius: accordionExpanded ? '8px 8px 0 0' : '8px',
                  minHeight: 56,
                  '&.Mui-expanded': {
                    minHeight: 56,
                  },
                },
                '& .MuiAccordionDetails-root': {
                  backgroundColor: 'white',
                  borderRadius: '0 0 8px 8px',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="search-filter-content"
                id="search-filter-header"
              >
                <Box display="flex" alignItems="center">
                  <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Search & Filter
                  </Typography>
                  
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3} alignItems="flex-end">
                  <Grid item xs={12}>
                    <Box>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          mb: 1, 
                          fontWeight: 600,
                          color: 'text.primary'
                        }}
                      >
                        Search Properties
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search by name, address, or city..."
                        value={search}
                        onChange={handleSearchChange}
                        size="medium"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            '&:hover': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              },
                            },
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                                borderWidth: 2,
                              },
                            },
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                          endAdornment: search && (
                            <InputAdornment position="end">
                              <IconButton 
                                onClick={handleClearSearch} 
                                size="small"
                                sx={{ 
                                  color: 'text.secondary',
                                  '&:hover': {
                                    color: 'text.primary',
                                    backgroundColor: 'action.hover'
                                  }
                                }}
                              >
                                <Clear />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <AreaFilter
                        selectedAreas={selectedAreas}
                        onChange={handleAreasChange}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={includeArchived}
                            onChange={handleIncludeArchivedChange}
                            color="primary"
                          />
                        }
                        label="Include Archived Properties"
                        sx={{ 
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: 'text.primary'
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
                
                {/* Filter Status */}
                {(search || includeArchived) && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Active filters:
                      </Typography>
                      
                      {search && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 2,
                            py: 0.5,
                            backgroundColor: 'primary.50',
                            color: 'primary.700',
                            borderRadius: 1,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                          }}
                        >
                          Search: "{search}"
                          <IconButton
                            size="small"
                            onClick={handleClearSearch}
                            sx={{ 
                              ml: 1, 
                              p: 0.25,
                              color: 'primary.600',
                              '&:hover': { 
                                backgroundColor: 'primary.100' 
                              }
                            }}
                          >
                            <Clear sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      )}
                      
                      {includeArchived && (
                        <Chip
                          label="Archived Properties"
                          size="small"
                          color="secondary"
                          variant="outlined"
                          onDelete={() => setIncludeArchived(false)}
                          deleteIcon={<Clear sx={{ fontSize: 14 }} />}
                          sx={{ fontSize: '0.875rem' }}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ) : (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: 'grey.50'
              }}
            >
              <Grid container spacing={3} alignItems="flex-end">
                <Grid item xs={12} md={8}>
                  <Box>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        mb: 1, 
                        fontWeight: 600,
                        color: 'text.primary'
                      }}
                    >
                      Search Properties
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Search by name, address, or city..."
                      value={search}
                      onChange={handleSearchChange}
                      size="medium"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                            },
                          },
                          '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                              borderWidth: 2,
                            },
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: search && (
                          <InputAdornment position="end">
                            <IconButton 
                              onClick={handleClearSearch} 
                              size="small"
                              sx={{ 
                                color: 'text.secondary',
                                '&:hover': {
                                  color: 'text.primary',
                                  backgroundColor: 'action.hover'
                                }
                              }}
                            >
                              <Clear />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box>
                    <AreaFilter
                      selectedAreas={selectedAreas}
                      onChange={handleAreasChange}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={12}>
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={includeArchived}
                          onChange={handleIncludeArchivedChange}
                          color="primary"
                        />
                      }
                      label="Include Archived Properties"
                      sx={{ 
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: 'text.primary'
                        }
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
              
              {/* Filter Status */}
              {(search || includeArchived) && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Active filters:
                    </Typography>
                    
                    {search && (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 2,
                          py: 0.5,
                          backgroundColor: 'primary.50',
                          color: 'primary.700',
                          borderRadius: 1,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        }}
                      >
                        Search: "{search}"
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          sx={{ 
                            ml: 1, 
                            p: 0.25,
                            color: 'primary.600',
                            '&:hover': { 
                              backgroundColor: 'primary.100' 
                            }
                          }}
                        >
                          <Clear sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    )}
                    
                    {includeArchived && (
                      <Chip
                        label="Archived Properties"
                        size="small"
                        color="secondary"
                        variant="outlined"
                        onDelete={() => setIncludeArchived(false)}
                        deleteIcon={<Clear sx={{ fontSize: 14 }} />}
                        sx={{ fontSize: '0.875rem' }}
                      />
                    )}
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </Box>

        {properties.length === 0 && !loading ? (
          <Alert 
            severity="info"
            action={
              <Button
                component={Link}
                to="/settings"
                size="small"
                color="inherit"
              >
                Sync Now
              </Button>
            }
          >
            {search ? 'No properties found matching your search.' : 'No properties found. Try syncing with Streamline to populate properties.'}
          </Alert>
        ) : properties.length === 0 && loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Address</TableCell>
                    <TableCell>Area</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>State</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {properties.map((property) => (
                    <TableRow key={property.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {property.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {property.address}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {property.city}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {property.state}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip
                          label={property.isActive ? 'Active' : 'Archived'}
                          size="small"
                          color={property.isActive ? 'success' : 'default'}
                          variant={property.isActive ? 'filled' : 'outlined'}
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Button
                            component={Link}
                            to={`/dashboard/properties/view/${property.id}`}
                            state={{
                              returnToPage: currentPage,
                              returnToSearch: search,
                              returnToAreas: selectedAreas
                            }}
                            color="primary"
                            size="small"
                            startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                            aria-label={`View info for ${property.name}`}
                          >
                            View Info
                          </Button>
                          {!['MAINTENANCE', 'CALL_CENTER'].includes(user?.role) && (
                            <Tooltip title="Edit Property" arrow>
                              <IconButton
                                component={Link}
                                to={`/dashboard/properties/edit/${property.id}`}
                                state={{
                                  returnToPage: currentPage,
                                  returnToSearch: search,
                                  returnToAreas: selectedAreas
                                }}
                                color="primary"
                                size="small"
                                aria-label={`Edit ${property.name}`}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box 
              display="flex" 
              justifyContent="space-between" 
              alignItems="center" 
              mt={3} 
              px={2}
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 },
              }}
            >
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  fontWeight: 500,
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                Showing {Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, totalItems)} - {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems} properties
              </Typography>
              {totalPages > 1 && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  '& .MuiPaginationItem-root': {
                    margin: '0 4px',
                    minWidth: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  },
                }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                    showFirstButton={!isMobile}
                    showLastButton={!isMobile}
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={isMobile ? 1 : 2}
                    size={isMobile ? "small" : "medium"}
                  />
                </Box>
              )}
            </Box>
          </>
        )}

      </Box>
    </Container>
  );
};

export default Properties; 